import React, { useState, useEffect, useRef } from 'react';
import { useContracts } from '../../hooks/useContract';
import { MATRIX_LEVELS, PACKAGE_PRICES } from '../../config/contracts';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import MatrixLevelDropdown from './MatrixLevelDropdown';
import { shortenAddress } from '../../utils/wallet';
import { formatTimestamp } from '../../utils/format';

// Fetches all 15 level member arrays for the selected package and lets user drill down.
// Also shows the true "Direct Team" count for this package (from directsInPackage), not the
// matrix-level-1 count (which caps at 2 due to matrix width).
const MatrixLevelsView = ({ userId, packageId }) => {
  const { storage } = useContracts();
  const [levels, setLevels] = useState([]);
  const [teamSize, setTeamSize] = useState(0);
  const [directCount, setDirectCount] = useState(0);
  const [directsList, setDirectsList] = useState([]); // all directs in this package
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // matrix level currently expanded
  const [directsExpanded, setDirectsExpanded] = useState(false);
  const [directsLoading, setDirectsLoading] = useState(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId || !packageId) return;
      const myRun = ++runIdRef.current;
      setLoading(true);
      setExpanded(null);
      setDirectsExpanded(false);
      setDirectsList([]);
      try {
        // Load matrix levels + team size + true direct count for this package in parallel
        const [size, directs, ...levelResults] = await Promise.all([
          storage.packageMatrixTeamSize(packageId, userId),
          storage.directsInPackage(userId, packageId).catch(() => 0),
          ...Array.from({ length: MATRIX_LEVELS }, (_, i) =>
            storage.getMatrixLevelMembers(packageId, userId, i + 1)
          )
        ]);
        if (cancelled || myRun !== runIdRef.current) return;
        setTeamSize(size.toNumber ? size.toNumber() : Number(size));
        setDirectCount(directs.toNumber ? directs.toNumber() : Number(directs));
        setLevels(levelResults.map((arr) =>
          arr.map((x) => x.toNumber ? x.toNumber() : Number(x))
        ));
      } catch (_) { /* ignore */ }
      finally {
        if (myRun === runIdRef.current) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [userId, packageId]);

  // Lazy-load the direct referrals list when the user expands the "Direct Team" section
  const loadDirectsList = async () => {
    if (directsList.length > 0) return; // already loaded
    setDirectsLoading(true);
    try {
      const allDirects = await storage.getUserReferrals(userId);
      const ids = allDirects.map((x) => x.toNumber ? x.toNumber() : Number(x));
      if (ids.length === 0) { setDirectsList([]); setDirectsLoading(false); return; }

      // Fetch each direct's data + their package status for THIS package
      const users = await Promise.all(ids.map((id) => storage.users(id).catch(() => null)));
      const pkgStatuses = await Promise.all(ids.map((id) =>
        storage.userPackages(id, packageId).catch(() => null)
      ));

      const filtered = [];
      users.forEach((u, i) => {
        const status = pkgStatuses[i];
        if (!u || !status) return;
        if (!status[0]) return; // isActive on THIS package
        filtered.push({
          id: u[1].toNumber ? u[1].toNumber() : Number(u[1]),
          wallet: u[0],
          joinTime: u[4].toNumber ? u[4].toNumber() : Number(u[4]),
          isBlocked: u[5],
          activationTime: status[1].toNumber ? status[1].toNumber() : Number(status[1])
        });
      });

      setDirectsList(filtered);
    } catch (_) { /* ignore */ }
    finally { setDirectsLoading(false); }
  };

  const toggleDirects = () => {
    const newState = !directsExpanded;
    setDirectsExpanded(newState);
    if (newState) loadDirectsList();
  };

  if (loading && levels.length === 0) {
    return (
      <Card title={`Package ${packageId} Matrix`}>
        <div className="text-center" style={{ padding: 40 }}><MiniLoader /></div>
      </Card>
    );
  }

  return (
    <>
      <div className="matrix-summary">
        <div className="matrix-summary-item">
          <div className="matrix-summary-label">Package</div>
          <div className="matrix-summary-value">P{packageId}</div>
        </div>
        <div className="matrix-summary-divider"></div>
        <div className="matrix-summary-item">
          <div className="matrix-summary-label">Price</div>
          <div className="matrix-summary-value">${PACKAGE_PRICES[packageId - 1]}</div>
        </div>
        <div className="matrix-summary-divider"></div>
        <div className="matrix-summary-item">
          <div className="matrix-summary-label">Direct Team</div>
          <div className="matrix-summary-value">{directCount}</div>
        </div>
        <div className="matrix-summary-divider"></div>
        <div className="matrix-summary-item">
          <div className="matrix-summary-label">Matrix Team</div>
          <div className="matrix-summary-value text-gradient">{teamSize}</div>
        </div>
      </div>

      {/* Direct Referrals in this package — expandable, shows ALL directs who bought this pkg */}
      <div className="level-row" style={{ marginBottom: 8 }}>
        <button
          className="level-header"
          onClick={toggleDirects}
          disabled={directCount === 0}
        >
          <div className="level-header-left">
            <div className="level-num" style={{ background: 'linear-gradient(135deg, #10D9A0, #22D3EE)' }}>
              👤
            </div>
            <div>
              <div className="level-title">Direct Referrals</div>
              <div className="level-sub">{directCount} direct{directCount !== 1 ? 's' : ''} activated Package {packageId}</div>
            </div>
          </div>
          <div className="level-header-right">
            {directCount > 0 && <span className="chip chip-cyan">{directCount}</span>}
            {directCount > 0 && (
              <div className={`level-chevron ${directsExpanded ? 'up' : ''}`}>▾</div>
            )}
          </div>
        </button>

        {directsExpanded && (
          <div className="level-body">
            {directsLoading && (
              <div className="text-center" style={{ padding: 20 }}><MiniLoader /></div>
            )}
            {!directsLoading && directsList.length === 0 && (
              <div className="text-muted text-center" style={{ padding: 16, fontSize: 13 }}>
                No direct referrals have activated Package {packageId} yet
              </div>
            )}
            {!directsLoading && directsList.length > 0 && (
              <div className="level-members">
                {directsList.map((m) => (
                  <div key={m.id} className="level-member">
                    <div className="level-member-avatar">
                      {String(m.id).charAt(0)}
                    </div>
                    <div className="level-member-info">
                      <div className="level-member-name">
                        {m.id}
                        {m.isBlocked && <span className="badge badge-error" style={{ marginLeft: 6, fontSize: 9 }}>Blocked</span>}
                      </div>
                      <div className="level-member-meta font-mono">
                        {shortenAddress(m.wallet)}
                        <span style={{ marginLeft: 6, opacity: 0.8 }}>· Since {formatTimestamp(m.activationTime).split(',')[0]}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Matrix levels — placement structure with max 2^level per level */}
      <div className="section-heading" style={{ marginTop: 16 }}>
        <span className="section-heading-bar"></span>
        Matrix Placement Levels
      </div>

      <div className="level-list">
        {levels.map((memberIds, idx) => (
          <MatrixLevelDropdown
            key={idx + 1}
            level={idx + 1}
            memberIds={memberIds}
            packageId={packageId}
            expanded={expanded === idx + 1}
            onToggle={() => setExpanded(expanded === idx + 1 ? null : idx + 1)}
          />
        ))}
      </div>
    </>
  );
};

export default MatrixLevelsView;
